import { useUser } from "@/zustand/store";
import { clsx, type ClassValue } from "clsx";
import moment from "moment";
import { ClipboardEvent } from "react";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export function getCardTransactionDisplayAmount(transaction: CardTransaction): {
  amount: number;
  currency: string;
} {
  if (transaction.type === "TransferIn" || transaction.type === "TransferOut") {
    return { amount: transaction.amount, currency: transaction.currency };
  }
  if (transaction.transactionCurrency === "USD") {
    return {
      amount: transaction.transactionAmount,
      currency: transaction.transactionCurrency,
    };
  }
  return { amount: transaction.amount, currency: transaction.currency };
}

export function formatAmount(
  amount: number | string | undefined,
  decimalPlaces: number = 2,
): string {
  const num = typeof amount === "number" ? amount : parseFloat(amount || "0");
  if (isNaN(num)) {
    return "0.00";
  }

  return num.toLocaleString("en-US", {
    minimumFractionDigits: decimalPlaces,
    maximumFractionDigits: decimalPlaces,
  });
}

export function formatAmountWithRate(
  amount: number | string | undefined,
  rate: number = 1,
  decimalPlaces: number = 2,
): string {
  const currency = useUser.getState().user?.preferredCurrency || "USD";
  const num = typeof amount === "number" ? amount : parseFloat(amount || "0");
  if (isNaN(num)) {
    return "0.00";
  }

  const convertedAmount = num / rate;

  const currencySymbols: Record<string, string> = {
    USD: "$",
    GBP: "£",
    EUR: "€",
  };

  const symbol = currencySymbols[currency?.toUpperCase()] || "";

  return (
    symbol +
    convertedAmount.toLocaleString("en-US", {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    })
  );
}

export function getCurrencyIconPath(
  currency: string,
  inactive: boolean = false,
) {
  const currencyIconMap: Record<string, Record<string, string>> = {
    "USDC-ERC20": {
      active: "/images/usdc.svg",
      inactive: "/images/usdc-inactive.svg",
    },
    "USDT-TRC20": {
      active: "/images/trcusdt.svg",
      inactive: "/images/trcusdt-inactive.svg",
    },
    "USDT-ERC20": {
      active: "/images/ercusdt.svg",
      inactive: "/images/ercusdt-inactive.svg",
    },
    BTC: { active: "/icons/btc.svg", inactive: "/icons/btc.svg" },
    ETH: { active: "/icons/eth.svg", inactive: "/icons/eth.svg" },
    USDT: { active: "/icons/usdt.svg", inactive: "/icons/usdt.svg" },
    USDC: { active: "/icons/usdc.svg", inactive: "/icons/usdc.svg" },
    ERC20: { active: "/icons/erc20.svg", inactive: "/icons/erc20.svg" },
    TRC20: { active: "/icons/trc20.png", inactive: "/icons/trc20.png" },
    USD: { active: "/icons/usd.svg", inactive: "/icons/usd.svg" },
    GBP: { active: "/icons/gbp.svg", inactive: "/icons/gbp.svg" },
    EUR: { active: "/icons/eur.svg", inactive: "/icons/eur.svg" },
  };

  const icons = currencyIconMap[currency.toUpperCase()];
  return icons ? icons[inactive ? "inactive" : "active"] : "";
}

export function formatAddress(address: string) {
  if (address.length < 10) {
    return address;
  }
  return `${address.slice(0, 6)}...${address.slice(-4)}`;
}

export const handlePaste = async <
  T extends HTMLInputElement | HTMLTextAreaElement,
>(
  setValue: (value: string) => void,
  event?: ClipboardEvent<T>,
): Promise<void> => {
  if (event) {
    event.preventDefault();
  }
  try {
    const text = await navigator.clipboard.readText();
    setValue(text);
  } catch {
    // ignore
  }
};

export function getDayDate(dayOfWeek: string): string {
  const weekdays = [
    "Sunday",
    "Monday",
    "Tuesday",
    "Wednesday",
    "Thursday",
    "Friday",
    "Saturday",
  ];

  const todayIndex = weekdays.indexOf(dayOfWeek);
  if (todayIndex === -1) {
    throw new Error("Invalid day string. Must be 'Sunday' to 'Saturday'.");
  }

  const daysUntilNextDay = (7 - todayIndex) % 7 || 7;
  const nextSunday = moment().add(daysUntilNextDay, "days");

  return nextSunday.format("YYYY-MM-DD");
}

export function getCurrencyFlagPath(currency: string) {
  const currencyFlagMap: Record<string, string> = {
    USD: "/images/us.png",
    GBP: "/images/uk.png",
    EUR: "/images/euro.png",
  };
  return currencyFlagMap[currency.toUpperCase()] || "";
}

export function getInitials(name: string) {
  return name
    .split(" ")
    .slice(0, 2)
    .map((part) => part.charAt(0).toUpperCase())
    .join("");
}
